import React from 'react';

import { ChatIcon, HomeIcon, SearchIcon, RovoIcon } from '@atlaskit/logo';
import { AppLogo } from '@atlaskit/navigation-system';
import { useIntl } from 'react-intl';

import { AnalyticsTopNavItem, useSendTopNavItemClick } from '@townsquare/analytics-nav4';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { chatRoute } from '@townsquare/routes/chat';
import { homeTabRoute } from '@townsquare/routes/hometab-view';
import { rovoSearchRoute } from '@townsquare/routes/rovo-search-view';
import { useRovoAppification, type RovoAppificationState } from '@townsquare/rovo/appification';

import { useIsOnFullscreenChat, useIsOnFullscreenSearch, useIsOnRovoSearch } from '../router/hooks';

function getProductHomeDetails(
	isChatRoute: boolean,
	isSearchRoute: boolean,
	rovoAppificationState: RovoAppificationState,
) {
	if (rovoAppificationState === 'enabled') {
		return {
			icon: <RovoIcon size="small" testId="rovo-icon" label="" shouldUseNewLogoDesign />,
			route: homeTabRoute,
			name: 'Rovo',
		};
	} else if (isChatRoute) {
		return {
			icon: <ChatIcon size="small" testId="chat-icon" label="" />,
			route: chatRoute,
			name: 'Chat',
		};
	} else if (isSearchRoute) {
		return {
			icon: <SearchIcon size="small" testId="search-icon" label="" />,
			route: rovoSearchRoute,
			name: 'Search',
		};
	} else {
		return {
			icon: <HomeIcon size="small" testId="home-icon" label="" />,
			route: homeTabRoute,
			name: 'Home',
		};
	}
}

export function ProductHome(): React.JSX.Element {
	const sendTopNavItemClick = useSendTopNavItemClick();
	const intl = useIntl();
	const routeDependencies = useRouteDependencies();
	const isOnFullscreenChat = useIsOnFullscreenChat();
	const isChatRoute = !!isOnFullscreenChat;
	const isOnFullscreenSearch = useIsOnFullscreenSearch();
	const isOnRovoSearch = useIsOnRovoSearch();
	const isSearchRoute = isOnFullscreenSearch || isOnRovoSearch;
	const { rovoAppificationState } = useRovoAppification();

	const { icon, name, route } = getProductHomeDetails(isChatRoute, isSearchRoute, rovoAppificationState);

	const href = generatePath(route, routeDependencies);

	return (
		<AppLogo
			icon={() => icon}
			name={name}
			href={href}
			onClick={() => {
				sendTopNavItemClick(AnalyticsTopNavItem.PRODUCT_LOGO);
			}}
			label={intl.formatMessage({
				id: 'townsquare.navigation.chrome-navigation.nav-logo-label',
				defaultMessage: 'Home page',
				description:
					'Accessible label for the product logo link in the top left of the navigation which takes users to the product home page',
			})}
		/>
	);
}
